import { useEffect, useRef } from "react";
import { Box } from "rebass/styled-components";

import { StyledMobileAppPromo } from "./styledComponents";
import { useAds } from "../../../Providers";

const AD_SLOT_ID = "side-rail-bottom-left";

const getAdsLogging = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("adsLogging") === "true";
};

const log = (...args: unknown[]) => {
  if (getAdsLogging()) {
    console.log("[MobileAppPromo]", ...args);
  }
};

const MobileAppPromo = () => {
  const { gpt, gptStatus, config } = useAds();
  const slotRef = useRef<typeof window.googletag.Slot | null>(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    const displayConfig = config?.display;
    const isEnabled = displayConfig?.enabled === true;

    log("Effect triggered", {
      gptExists: !!gpt,
      gptStatus,
      displayConfigExists: !!displayConfig,
      isEnabled,
      hasInitialized: hasInitializedRef.current,
    });

    if (!gpt || gptStatus !== "ready" || !displayConfig || !isEnabled) {
      log("Prerequisites not met, skipping");
      return;
    }

    hasInitializedRef.current = true;
    log("Starting slot initialization");

    gpt.cmd.push(() => {
      const adElement = document.getElementById(AD_SLOT_ID);
      if (!adElement) {
        log("Ad container element not found in DOM, will retry");
        hasInitializedRef.current = false;
        return;
      }

      log("Defining slot", {
        path: `/${displayConfig.networkId}/${displayConfig.sideRailAdUnit}`,
        size: [[420, 200]],
        slotId: AD_SLOT_ID,
      });

      const slot = gpt.defineSlot(
        `/${displayConfig.networkId}/${displayConfig.sideRailAdUnit}`,
        [[420, 200]],
        AD_SLOT_ID,
      );

      if (!slot) {
        log("Failed to define slot");
        hasInitializedRef.current = false;
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const testAds = params.get("testAds");
      if (testAds) {
        log("Setting test targeting", testAds);
        slot.setTargeting("test", testAds);
      }

      const viewerSource = window.location.href.includes("iframe")
        ? "iframe"
        : "website";
      log("Setting viewer_source targeting", viewerSource);
      slot.setTargeting("viewer_source", viewerSource);

      slot.addService(gpt.pubads());
      slotRef.current = slot;

      log("Calling gpt.display()");
      gpt.display(AD_SLOT_ID);

      log("Refreshing slot to fetch ad");
      gpt.pubads().refresh([slot]);

      log("Slot initialization complete");
    });
  }, [gpt, gptStatus, config?.display]);

  useEffect(() => {
    return () => {
      if (gpt && slotRef.current) {
        const slotToDestroy = slotRef.current;
        log("Cleanup: destroying slot");
        gpt.cmd.push(() => {
          gpt.destroySlots([slotToDestroy]);
        });
        slotRef.current = null;
      }
      hasInitializedRef.current = false;
    };
  }, [gpt]);

  return (
    <StyledMobileAppPromo>
      <Box id={AD_SLOT_ID} style={{ width: "100%" }} />
    </StyledMobileAppPromo>
  );
};

export { MobileAppPromo };
